import { useEffect, useMemo, useState } from "react";
import { PhotonBriefHeader } from "./components/PhotonBriefHeader";
import AtlasPage from "./features/atlas/AtlasPage";
import AtlasCalibrationPage from "./features/atlas/AtlasCalibrationPage";

type DirectionMembership = {
  direction_id: string;
  role: string;
  confidence?: number;
};

type PaperSummary = {
  paper_id: string;
  doi: string | null;
  title: string;
  published_year: number | null;
  metadata_status: string;
  pdf_status: string;
  assertions_count: number;
  directions: DirectionMembership[];
};

type PaperIndex = {
  pilot_version: string;
  paper_count: number;
  pdf_attached_count: number;
  papers: PaperSummary[];
};

type PaperDetail = {
  paper_id: string;
  doi: string | null;
  title: string;
  published_year: number | null;
  container_title: string | null;
  directions: DirectionMembership[];
  assertions: Array<{
    assertion_id: string;
    field_name: string;
    canonical_value: string;
    page_number: number;
    quote: string;
  }>;
};

type ExtractionItem = {
  item_id: string;
  field_name: string;
  canonical_value: string;
  review_status: string;
  evidence: Array<{
    evidence_id: string;
    page_number: number;
    quote: string;
  }>;
};

type ExtractionPayload = {
  paper_id: string;
  schema_version: string;
  item_count: number;
  fields: Record<string, ExtractionItem[]>;
};

type SearchDocument = {
  paper_id: string;
  doi: string | null;
  title: string;
  journal: string | null;
  published_year: number | null;
  directions: DirectionMembership[];
  fields: Record<
    string,
    Array<{
      item_id: string;
      canonical_value: string;
      review_status: string;
      evidence_ids: string[];
    }>
  >;
  token_count: number;
};

type SearchIndex = {
  schema_version: string;
  source_sha256: string;
  paper_count: number;
  item_count: number;
  evidence_link_count: number;
  direction_ids: string[];
  term_count: number;
  documents: SearchDocument[];
  postings: Record<string, Array<{ paper_id: string; score: number }>>;
};

type GraphNode = {
  id: string;
  type: "paper" | "direction" | "extraction_item" | "evidence";
  paper_id?: string;
  direction_id?: string;
  label: string;
  published_year?: number | null;
  field_name?: string;
};

type GraphEdge = {
  id: string;
  type:
    | "PAPER_CLASSIFIED_AS_DIRECTION"
    | "PAPER_HAS_EXTRACTION_ITEM"
    | "EXTRACTION_ITEM_SUPPORTED_BY_EVIDENCE";
  source: string;
  target: string;
  paper_id: string;
  semantics: string;
};

type GraphPayload = {
  schema_version: string;
  node_count: number;
  edge_count: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
};

type TechnicalChain = {
  paper_id: string;
  doi: string | null;
  title: string;
  semantics: "presentation_order_not_causal";
  stages: Array<{
    field_name: string;
    items: Array<{ item_id: string; canonical_value: string }>;
  }>;
  metrics: Array<{ item_id: string; canonical_value: string }>;
  item_ref_count: number;
};

type TechnicalChains = {
  paper_count: number;
  item_ref_count: number;
  semantics: "presentation_order_not_causal";
  chains: TechnicalChain[];
};

type Route =
  | { kind: "home" }
  | { kind: "search" }
  | { kind: "graph" }
  | { kind: "atlas" }
  | { kind: "atlas-calibration" }
  | { kind: "paper"; paperId: string };

const directions = [
  ["D1", "本征各向异性材料与线偏振探测", "Intrinsically Anisotropic Materials and Linear Polarization Detection"],
  ["D2", "手性材料与圆偏振探测", "Chiral Materials and Circular Polarization Detection"],
  ["D3", "异质结与多功能偏振器件", "Heterojunction and Multifunctional Polarization Devices"],
  ["D4", "人工结构、超表面与集成偏振探测", "Artificial Structures, Metasurfaces and Integrated Polarization Detection"],
  ["D5", "阵列、全 Stokes 与多维偏振成像", "Arrays, Full-Stokes and Multidimensional Polarization Imaging"],
  ["D6", "计算偏振成像与 AI 视觉", "Computational Polarization Imaging and AI Vision"],
  ["D7", "仿生、神经形态与传感器内计算", "Bioinspired, Neuromorphic and In-Sensor Polarization Computing"],
  ["D8", "偏振探测应用系统", "Polarization Detection Application Systems"],
] as const;

const extractionFieldLabels: Record<string, string> = {
  problem: "Problem",
  material: "Material",
  mechanism: "Mechanism",
  device_architecture: "Device architecture",
  excitation_detection: "Excitation / detection",
  polarization_modality: "Polarization modality",
  method: "Method",
  key_metric: "Key metric",
  application: "Application",
  limitation: "Limitation",
  novelty: "Novelty",
};

function parseRoute(): Route {
  const hash = window.location.hash;
  if (hash.startsWith("#/atlas-calibration")) return { kind: "atlas-calibration" };
  if (hash.startsWith("#/atlas")) return { kind: "atlas" };
  if (hash === "#/search") return { kind: "search" };
  if (hash === "#/graph") return { kind: "graph" };
  const paperMatch = hash.match(/^#\/papers\/([^/]+)$/);
  if (paperMatch) {
    return { kind: "paper", paperId: decodeURIComponent(paperMatch[1]) };
  }
  return { kind: "atlas" };
}

function tokenize(value: string): string[] {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .match(/[\p{L}\p{N}]+/gu) ?? [];
}

function DirectionBadges({ values }: { values: DirectionMembership[] }) {
  return (
    <div className="badges">
      {values.map((item) => (
        <span
          className={item.role === "primary" ? "badge primary" : "badge"}
          key={`${item.direction_id}-${item.role}`}
        >
          {item.direction_id}
        </span>
      ))}
    </div>
  );
}

function Home() {
  const [index, setIndex] = useState<PaperIndex | null>(null);
  const [searchIndex, setSearchIndex] = useState<SearchIndex | null>(null);
  const [graph, setGraph] = useState<GraphPayload | null>(null);

  useEffect(() => {
    Promise.all([
      fetch("/data/papers/index.json", { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
      fetch("/data/search/index.json", { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
      fetch("/data/graphs/graph.json", { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
    ])
      .then(([paperData, searchData, graphData]) => {
        setIndex(paperData);
        setSearchIndex(searchData);
        setGraph(graphData);
      })
      .catch(() => {
        setIndex(null);
        setSearchIndex(null);
        setGraph(null);
      });
  }, []);

  return (
    <main>
      <section className="hero">
        <p className="eyebrow">Evidence-centered research map</p>
        <h1>从论文原文证据走向可搜索、可追溯的偏振探测研究地图</h1>
        <p className="lede">
          PhotonBrief 只展示通过学术准入、去重和 PDF Evidence 门的真实论文。
          ScientificExtraction、方向分类与图关系都保留可审计 provenance。
        </p>
        <div className="hero-actions">
          <a className="button primary-button" href="#/search">搜索科研内容</a>
          <a className="button" href="#/graph">浏览研究图谱</a>
        </div>
      </section>

      <section className="metric-strip" aria-label="Corpus summary">
        <article><strong>{index?.paper_count ?? "—"}</strong><span>Papers</span></article>
        <article><strong>{index?.pdf_attached_count ?? "—"}</strong><span>PDFs</span></article>
        <article><strong>{searchIndex?.item_count ?? "—"}</strong><span>Structured items</span></article>
        <article><strong>{graph?.edge_count ?? "—"}</strong><span>Typed relations</span></article>
      </section>

      <section className="section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Trusted corpus</p>
            <h2>Canonical evidence-reviewed papers</h2>
          </div>
          <a className="text-link" href="#/search">Search all →</a>
        </div>
        <div className="paper-grid">
          {index?.papers.map((paper) => (
            <article className="paper-card" key={paper.paper_id}>
              <div className="paper-meta">
                <span>{paper.published_year ?? "—"}</span>
                <DirectionBadges values={paper.directions} />
              </div>
              <h3>
                <a href={`#/papers/${paper.paper_id}`}>{paper.title}</a>
              </h3>
              <p className="doi">{paper.doi}</p>
              <div className="status-row">
                <span>{paper.assertions_count} Evidence-backed claims</span>
                <span>{paper.pdf_status}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Research taxonomy</p>
            <h2>D1–D8 研究方向</h2>
          </div>
          <span className="count">8 canonical directions</span>
        </div>
        <div className="direction-grid">
          {directions.map(([id, zh, en]) => (
            <article className="direction-card" key={id}>
              <div className="direction-id">{id}</div>
              <h3>{zh}</h3>
              <p>{en}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function rankSearch(
  index: SearchIndex,
  query: string,
  direction: string,
): Array<{ document: SearchDocument; score: number; matchedTerms: string[] }> {
  const tokens = tokenize(query);
  const byId = new Map(index.documents.map((document) => [document.paper_id, document]));
  const scores = new Map<string, number>();
  const matched = new Map<string, Set<string>>();

  if (tokens.length === 0) {
    index.documents.forEach((document) => scores.set(document.paper_id, 0));
  } else {
    tokens.forEach((token) => {
      (index.postings[token] ?? []).forEach((posting) => {
        scores.set(posting.paper_id, (scores.get(posting.paper_id) ?? 0) + posting.score);
        const terms = matched.get(posting.paper_id) ?? new Set<string>();
        terms.add(token);
        matched.set(posting.paper_id, terms);
      });
    });

    if (scores.size === 0) {
      const needle = query.normalize("NFKC").toLocaleLowerCase();
      index.documents.forEach((document) => {
        const fieldText = Object.values(document.fields)
          .flat()
          .map((item) => item.canonical_value)
          .join(" ");
        const haystack = `${document.title} ${document.doi ?? ""} ${fieldText}`.toLocaleLowerCase();
        if (haystack.includes(needle)) scores.set(document.paper_id, 1);
      });
    }
  }

  return [...scores.entries()]
    .map(([paperId, score]) => ({
      document: byId.get(paperId)!,
      score,
      matchedTerms: [...(matched.get(paperId) ?? [])].sort(),
    }))
    .filter(({ document }) =>
      direction === "ALL"
        ? true
        : document.directions.some((item) => item.direction_id === direction),
    )
    .sort(
      (a, b) =>
        b.score - a.score ||
        (b.document.published_year ?? 0) - (a.document.published_year ?? 0) ||
        a.document.title.localeCompare(b.document.title),
    );
}

function SearchPage() {
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [query, setQuery] = useState("");
  const [direction, setDirection] = useState("ALL");

  useEffect(() => {
    fetch("/data/search/index.json", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then(setIndex)
      .catch(() => setIndex(null));
  }, []);

  const results = useMemo(
    () => (index ? rankSearch(index, query, direction) : []),
    [index, query, direction],
  );

  return (
    <main>
      <section className="page-hero compact">
        <p className="eyebrow">Static scientific search</p>
        <h1>搜索论文、器件、材料、机制与指标</h1>
        <p className="lede">
          搜索索引由本地 Builder 从当前 canonical public projection 确定性生成。
        </p>
      </section>

      <section className="search-panel">
        <label className="search-box">
          <span>Search query</span>
          <input
            autoFocus
            value={query}
            onChange={(event: { target: { value: string } }) => setQuery(event.target.value)}
            placeholder="metasurface, full Stokes, ferroelectric, neuromorphic…"
          />
        </label>
        <div className="filter-row" aria-label="Direction filters">
          <button
            className={direction === "ALL" ? "filter active" : "filter"}
            onClick={() => setDirection("ALL")}
            type="button"
          >
            All
          </button>
          {directions.map(([id]) => (
            <button
              className={direction === id ? "filter active" : "filter"}
              key={id}
              onClick={() => setDirection(id)}
              type="button"
            >
              {id}
            </button>
          ))}
        </div>
      </section>

      <section className="section search-results">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Results</p>
            <h2>{results.length} papers</h2>
          </div>
          <span className="count">
            {index ? `${index.term_count} indexed terms` : "loading"}
          </span>
        </div>

        <div className="result-list">
          {results.map(({ document, score, matchedTerms }) => (
            <article className="result-card" key={document.paper_id}>
              <div className="paper-meta">
                <span>{document.published_year ?? "—"}</span>
                <DirectionBadges values={document.directions} />
                {query && <span>score {score}</span>}
              </div>
              <h3>
                <a href={`#/papers/${document.paper_id}`}>{document.title}</a>
              </h3>
              <p className="doi">{document.doi}</p>
              {matchedTerms.length > 0 && (
                <p className="matched">Matched: {matchedTerms.join(" · ")}</p>
              )}
              <div className="field-preview">
                {Object.entries(document.fields)
                  .slice(0, 4)
                  .map(([field, values]) => (
                    <div key={field}>
                      <strong>{extractionFieldLabels[field] ?? field}</strong>
                      <span>{values.map((item) => item.canonical_value).join(" · ")}</span>
                    </div>
                  ))}
              </div>
            </article>
          ))}
          {index && results.length === 0 && (
            <div className="empty-state">
              No indexed paper matches this query and direction filter.
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function GraphExplorer() {
  const [graph, setGraph] = useState<GraphPayload | null>(null);
  const [chains, setChains] = useState<TechnicalChains | null>(null);
  const [direction, setDirection] = useState("D1");
  const [selectedPaper, setSelectedPaper] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch("/data/graphs/graph.json", { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
      fetch("/data/graphs/technical_chains.json", { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
    ])
      .then(([graphData, chainData]) => {
        setGraph(graphData);
        setChains(chainData);
      })
      .catch(() => {
        setGraph(null);
        setChains(null);
      });
  }, []);

  const paperNodes = useMemo(
    () => graph?.nodes.filter((node) => node.type === "paper") ?? [],
    [graph],
  );

  const directionEdges = useMemo(
    () =>
      graph?.edges.filter(
        (edge) =>
          edge.type === "PAPER_CLASSIFIED_AS_DIRECTION" &&
          edge.target === `direction:${direction}`,
      ) ?? [],
    [graph, direction],
  );

  const visiblePapers = useMemo(() => {
    const ids = new Set(directionEdges.map((edge) => edge.paper_id));
    return paperNodes
      .filter((node) => node.paper_id && ids.has(node.paper_id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [directionEdges, paperNodes]);

  useEffect(() => {
    if (visiblePapers.length > 0 && !visiblePapers.some((node) => node.paper_id === selectedPaper)) {
      setSelectedPaper(visiblePapers[0].paper_id ?? null);
    }
  }, [visiblePapers, selectedPaper]);

  const selectedChain = chains?.chains.find((chain) => chain.paper_id === selectedPaper) ?? null;
  const height = Math.max(360, visiblePapers.length * 62 + 80);

  return (
    <main>
      <section className="page-hero compact">
        <p className="eyebrow">Evidence-derived structural graph</p>
        <h1>研究方向 → 论文 → 结构化科研项 → Evidence</h1>
        <p className="lede">
          v1 只展示可信数据中已经明确存在的结构关系。Technical chain 的字段顺序用于呈现，
          不代表科学因果。
        </p>
      </section>

      <section className="metric-strip">
        <article><strong>{graph?.node_count ?? "—"}</strong><span>Graph nodes</span></article>
        <article><strong>{graph?.edge_count ?? "—"}</strong><span>Typed edges</span></article>
        <article><strong>{chains?.paper_count ?? "—"}</strong><span>Technical chains</span></article>
        <article><strong>{chains?.item_ref_count ?? "—"}</strong><span>Chain item refs</span></article>
      </section>

      <section className="section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Direction explorer</p>
            <h2>{direction} connected papers</h2>
          </div>
          <span className="count">{visiblePapers.length} papers</span>
        </div>

        <div className="filter-row">
          {directions.map(([id]) => (
            <button
              className={direction === id ? "filter active" : "filter"}
              key={id}
              onClick={() => setDirection(id)}
              type="button"
            >
              {id}
            </button>
          ))}
        </div>

        <div className="graph-layout">
          <div className="graph-canvas" role="img" aria-label={`${direction} paper graph`}>
            <svg viewBox={`0 0 900 ${height}`} height={height}>
              <rect className="graph-direction-node" x="32" y={height / 2 - 28} width="160" height="56" rx="12" />
              <text className="graph-direction-label" x="112" y={height / 2 + 5} textAnchor="middle">
                {direction}
              </text>
              {visiblePapers.map((paper, index) => {
                const y = 48 + index * 62;
                return (
                  <g key={paper.id}>
                    <line className="graph-edge" x1="192" y1={height / 2} x2="338" y2={y} />
                    <circle className="graph-paper-node" cx="356" cy={y} r="8" />
                    <text
                      className="graph-paper-label"
                      x="378"
                      y={y + 5}
                      onClick={() => setSelectedPaper(paper.paper_id ?? null)}
                    >
                      {paper.label.length > 66 ? `${paper.label.slice(0, 66)}…` : paper.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          <aside className="chain-panel">
            <p className="eyebrow">Technical chain</p>
            {selectedChain ? (
              <>
                <h3>
                  <a href={`#/papers/${selectedChain.paper_id}`}>{selectedChain.title}</a>
                </h3>
                <p className="noncausal">presentation_order_not_causal</p>
                <div className="chain-list">
                  {selectedChain.stages.map((stage) => (
                    <div className="chain-stage" key={stage.field_name}>
                      <strong>{extractionFieldLabels[stage.field_name] ?? stage.field_name}</strong>
                      {stage.items.map((item) => (
                        <span key={item.item_id}>{item.canonical_value}</span>
                      ))}
                    </div>
                  ))}
                  {selectedChain.metrics.length > 0 && (
                    <div className="chain-stage metric-stage">
                      <strong>Key metrics</strong>
                      {selectedChain.metrics.map((item) => (
                        <span key={item.item_id}>{item.canonical_value}</span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">Select a connected paper.</div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
}

function PaperPage({ paperId }: { paperId: string }) {
  const [paper, setPaper] = useState<PaperDetail | null>(null);
  const [extraction, setExtraction] = useState<ExtractionPayload | null>(null);

  useEffect(() => {
    Promise.all([
      fetch(`/data/papers/${paperId}.json`, { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
      fetch(`/data/extractions/${paperId}.json`, { cache: "no-store" }).then((response) =>
        response.ok ? response.json() : null,
      ),
    ])
      .then(([paperData, extractionData]) => {
        setPaper(paperData);
        setExtraction(extractionData);
      })
      .catch(() => {
        setPaper(null);
        setExtraction(null);
      });
  }, [paperId]);

  return (
    <main className="paper-detail">
      <a className="back-link" href="#">← 返回研究地图</a>
      {!paper && <div className="empty-state">Paper data unavailable.</div>}
      {paper && (
        <>
          <DirectionBadges values={paper.directions} />
          <h1 className="paper-title">{paper.title}</h1>
          <p className="lede">
            {paper.container_title} · {paper.published_year} · {paper.doi}
          </p>

          <section className="section">
            <div className="section-heading">
              <div>
                <p className="eyebrow">ScientificExtraction v1</p>
                <h2>结构化科研记录</h2>
              </div>
              <span className="count">{extraction?.item_count ?? 0} items</span>
            </div>
            <div className="extraction-grid">
              {extraction &&
                Object.entries(extraction.fields)
                  .filter(([, values]) => values.length > 0)
                  .map(([field, values]) => (
                    <article className="extraction-card" key={field}>
                      <h3>{extractionFieldLabels[field] ?? field}</h3>
                      {values.map((item) => (
                        <div className="extraction-value" key={item.item_id}>
                          <p>{item.canonical_value}</p>
                          <span>{item.evidence.length} Evidence link(s)</span>
                        </div>
                      ))}
                    </article>
                  ))}
            </div>
          </section>

          <section className="section">
            <div className="section-heading">
              <div>
                <p className="eyebrow">PDF exact Evidence</p>
                <h2>Evidence-backed assertions</h2>
              </div>
              <span className="count">{paper.assertions.length}</span>
            </div>
            <div className="assertion-list">
              {paper.assertions.map((item) => (
                <article className="assertion-card" key={item.assertion_id}>
                  <span className="assertion-field">{item.field_name}</span>
                  <h3>{item.canonical_value}</h3>
                  <p className="evidence-quote">“{item.quote}”</p>
                  <span className="evidence-page">PDF page {item.page_number}</span>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

export default function App() {
  const [route, setRoute] = useState<Route>(parseRoute());

  useEffect(() => {
    const onHashChange = () => {
      setRoute(parseRoute());
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div className={`app-shell ${route.kind === "atlas" ? "atlas-shell" : ""}`}>
      <PhotonBriefHeader active={route.kind === "atlas-calibration" ? "method" : route.kind === "atlas" ? "atlas" : ""} />

      {route.kind === "home" && <Home />}
      {route.kind === "atlas" && <AtlasPage />}
      {route.kind === "atlas-calibration" && <AtlasCalibrationPage />}
      {route.kind === "search" && <SearchPage />}
      {route.kind === "graph" && <GraphExplorer />}
      {route.kind === "paper" && <PaperPage paperId={route.paperId} />}

      {route.kind !== "atlas" && <footer>
        <span>PhotonBrief</span>
        <span>Evidence-centered · local-first · static runtime</span>
      </footer>}
    </div>
  );
}
